import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Search, Edit, Trash2, Eye } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from '../components/ui/form';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMembers, useCreateMember, useUpdateMember, useDeleteMember } from '../hooks/useQueries';
import type { Member, CreateMemberRequest, UpdateMemberRequest } from '../services';
import { formatThaiPhoneNumber } from '../utils/thai';
import { toast } from 'sonner';
import { ErrorCard } from '../components/error';
import { SkeletonTable } from '../components/ui/skeleton';

const memberFormSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  email: z.string().email().optional().or(z.literal('')),
  phoneNumber: z.string().min(10, 'Phone number is required'),
  lineId: z.string().optional(),
  role: z.enum(['member', 'treasurer', 'board']),
});

type MemberFormData = z.infer<typeof memberFormSchema>;

export function MembersPage() {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMember, setSelectedMember] = useState<Member | null>(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [memberToDelete, setMemberToDelete] = useState<Member | null>(null);

  // Fetch members
  const { data, isLoading, error } = useMembers({ search: searchQuery });

  // Member mutations with custom success handlers
  const createMemberMutation = useCreateMember();
  const updateMemberMutation = useUpdateMember();
  const deleteMemberMutation = useDeleteMember();

  // Create form
  const createForm = useForm<MemberFormData>({
    resolver: zodResolver(memberFormSchema),
    defaultValues: {
      name: '',
      email: '',
      phoneNumber: '',
      lineId: '',
      role: 'member',
    },
  });

  // Edit form
  const editForm = useForm<MemberFormData>({
    resolver: zodResolver(memberFormSchema),
    defaultValues: {
      name: '',
      email: '',
      phoneNumber: '',
      lineId: '',
      role: 'member',
    },
  });

  // Handle success states
  useEffect(() => {
    if (createMemberMutation.isSuccess) {
      setIsCreateDialogOpen(false);
      toast.success(t('members.createSuccess'));
      createForm.reset();
    }
  }, [createMemberMutation.isSuccess, createForm, t]);

  useEffect(() => {
    if (updateMemberMutation.isSuccess) {
      setIsEditDialogOpen(false);
      toast.success(t('members.updateSuccess'));
      editForm.reset();
    }
  }, [updateMemberMutation.isSuccess, editForm, t]);

  useEffect(() => {
    if (deleteMemberMutation.isSuccess) {
      setIsDeleteDialogOpen(false);
      setMemberToDelete(null);
      toast.success(t('members.deleteSuccess'));
    }
  }, [deleteMemberMutation.isSuccess, t]);

  // Handle error states
  useEffect(() => {
    if (createMemberMutation.isError) {
      toast.error(t('members.createError'));
    }
  }, [createMemberMutation.isError, t]);

  useEffect(() => {
    if (updateMemberMutation.isError) {
      toast.error(t('members.updateError'));
    }
  }, [updateMemberMutation.isError, t]);

  useEffect(() => {
    if (deleteMemberMutation.isError) {
      toast.error(t('members.deleteError'));
    }
  }, [deleteMemberMutation.isError, t]);


  const handleCreateSubmit = (data: MemberFormData) => {
    const createData: CreateMemberRequest = {
      name: data.name,
      email: data.email || undefined,
      phoneNumber: data.phoneNumber,
      lineId: data.lineId || undefined,
      role: data.role,
    };
    createMemberMutation.mutate(createData);
  };

  const handleEditSubmit = (data: MemberFormData) => {
    if (!selectedMember) return;
    
    const updateData: UpdateMemberRequest = {
      name: data.name,
      email: data.email || undefined,
      phoneNumber: data.phoneNumber,
      lineId: data.lineId || undefined,
      role: data.role,
      status: selectedMember.status,
    };
    updateMemberMutation.mutate({ memberId: selectedMember.memberId, data: updateData });
  };

  const handleEdit = (member: Member) => {
    setSelectedMember(member);
    editForm.reset({
      name: member.name,
      email: member.email || '',
      phoneNumber: member.phoneNumber || '',
      lineId: member.lineId || '',
      role: member.role,
    });
    setIsEditDialogOpen(true);
  };

  const handleView = (member: Member) => {
    setSelectedMember(member);
    setIsViewDialogOpen(true);
  };

  const handleDeleteClick = (member: Member) => {
    setMemberToDelete(member);
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (memberToDelete) {
      deleteMemberMutation.mutate(memberToDelete.memberId);
    }
  };

  const getRoleDisplay = (role: string) => {
    switch (role) {
      case 'board':
        return t('members.roles.board');
      case 'treasurer':
        return t('members.roles.treasurer');
      default:
        return t('members.roles.member');
    }
  };

  const getStatusDisplay = (status: string) => {
    switch (status) {
      case 'active':
        return t('members.status.active');
      case 'inactive':
        return t('members.status.inactive');
      case 'suspended':
        return t('members.status.suspended');
      default:
        return status;
    }
  };

  if (error) {
    return (
      <div className="p-6">
        <div className="mb-6">
          <h1 className="text-2xl font-semibold text-gray-900">{t('members.title')}</h1>
          <p className="text-gray-600">{t('members.subtitle')}</p>
        </div>
        <ErrorCard error={error} onRetry={() => window.location.reload()} />
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-900">{t('members.title')}</h1>
        <p className="text-gray-600">{t('members.subtitle')}</p>
      </div>

      <Card className="p-6">
        <div className="mb-4 flex gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <Input
                type="search"
                placeholder={t('members.searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
          <Button onClick={() => setIsCreateDialogOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            {t('members.addMember')}
          </Button>
        </div>

        {isLoading ? (
          <SkeletonTable rows={5} />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('members.table.memberId')}</TableHead>
                <TableHead>{t('members.table.name')}</TableHead>
                <TableHead>{t('members.table.phoneNumber')}</TableHead>
                <TableHead>{t('members.table.role')}</TableHead>
                <TableHead>{t('members.table.status')}</TableHead>
                <TableHead className="text-right">{t('members.table.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data?.items.map((member) => (
                <TableRow key={member.memberId}>
                  <TableCell>{member.memberId.slice(-8)}</TableCell>
                  <TableCell>{member.name}</TableCell>
                  <TableCell>{formatThaiPhoneNumber(member.phoneNumber || '')}</TableCell>
                  <TableCell>{getRoleDisplay(member.role)}</TableCell>
                  <TableCell>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      member.status === 'active' 
                        ? 'bg-green-100 text-green-800' 
                        : member.status === 'inactive'
                        ? 'bg-gray-100 text-gray-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {getStatusDisplay(member.status)}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex gap-2 justify-end">
                      <Button variant="ghost" size="sm" onClick={() => handleView(member)}>
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(member)}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDeleteClick(member)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      {/* Create Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>{t('members.addMember')}</DialogTitle>
          </DialogHeader>
          <Form {...createForm}>
            <form onSubmit={createForm.handleSubmit(handleCreateSubmit)} className="space-y-4">
              <FormField
                control={createForm.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('members.form.name')}</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={createForm.control}
                name="phoneNumber"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('members.form.phoneNumber')}</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="0812345678" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={createForm.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('members.form.email')}</FormLabel>
                    <FormControl>
                      <Input {...field} type="email" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={createForm.control}
                name="lineId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('members.form.lineId')}</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={createForm.control}
                name="role"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('members.form.role')}</FormLabel>
                    <FormControl>
                      <select {...field} className="w-full rounded-md border border-gray-300 px-3 py-2">
                        <option value="member">{t('members.roles.member')}</option>
                        <option value="treasurer">{t('members.roles.treasurer')}</option>
                        <option value="board">{t('members.roles.board')}</option>
                      </select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                  {t('common.cancel')}
                </Button>
                <Button type="submit" disabled={createMemberMutation.isPending}>
                  {createMemberMutation.isPending ? t('common.saving') : t('common.save')}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>{t('members.editMember')}</DialogTitle>
          </DialogHeader>
          <Form {...editForm}>
            <form onSubmit={editForm.handleSubmit(handleEditSubmit)} className="space-y-4">
              <FormField
                control={editForm.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('members.form.name')}</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={editForm.control}
                name="phoneNumber"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('members.form.phoneNumber')}</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="0812345678" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={editForm.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('members.form.email')}</FormLabel>
                    <FormControl>
                      <Input {...field} type="email" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={editForm.control}
                name="lineId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('members.form.lineId')}</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={editForm.control}
                name="role"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('members.form.role')}</FormLabel>
                    <FormControl>
                      <select {...field} className="w-full rounded-md border border-gray-300 px-3 py-2">
                        <option value="member">{t('members.roles.member')}</option>
                        <option value="treasurer">{t('members.roles.treasurer')}</option>
                        <option value="board">{t('members.roles.board')}</option>
                      </select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsEditDialogOpen(false)}>
                  {t('common.cancel')}
                </Button>
                <Button type="submit" disabled={updateMemberMutation.isPending}>
                  {updateMemberMutation.isPending ? t('common.saving') : t('common.save')}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>{t('members.viewMember')}</DialogTitle>
          </DialogHeader>
          {selectedMember && (
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-500">{t('members.form.memberId')}</label>
                <p className="mt-1">{selectedMember.memberId}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">{t('members.form.name')}</label>
                <p className="mt-1">{selectedMember.name}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">{t('members.form.phoneNumber')}</label>
                <p className="mt-1">{formatThaiPhoneNumber(selectedMember.phoneNumber || '')}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">{t('members.form.email')}</label>
                <p className="mt-1">{selectedMember.email || '-'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">{t('members.form.lineId')}</label>
                <p className="mt-1">{selectedMember.lineId || '-'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">{t('members.form.role')}</label>
                <p className="mt-1">{getRoleDisplay(selectedMember.role)}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">{t('members.form.status')}</label>
                <p className="mt-1">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    selectedMember.status === 'active' 
                      ? 'bg-green-100 text-green-800' 
                      : selectedMember.status === 'inactive'
                      ? 'bg-gray-100 text-gray-800'
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {getStatusDisplay(selectedMember.status)}
                  </span>
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">{t('members.form.joinedDate')}</label>
                <p className="mt-1">{new Date(selectedMember.joinDate).toLocaleDateString('th-TH')}</p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setIsViewDialogOpen(false)}>
              {t('common.close')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>{t('members.deleteConfirmTitle')}</DialogTitle>
          </DialogHeader>
          <p className="text-gray-600">
            {t('members.deleteConfirmMessage', { name: memberToDelete?.name })}
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDeleteConfirm}
              disabled={deleteMemberMutation.isPending}
            >
              {deleteMemberMutation.isPending ? t('common.deleting') : t('common.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}